import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MultiFilterModule,
  PivotModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  SideBarModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import React, { forwardRef, useMemo } from "react";

import AgGridClickableTable from "../../../../components/agGrid/tables/AgGridClickableTable";
import PropertyAssociatedCommentsBadge from "../../../../components/common/Loan/PropertyAssociatedCommentsBadge";
import { useDynamicColumnDefs } from "../../../../_src_shared/utils/agGrid/hooks/useDynamicColumnDefs";
import { ChevronRightOutlined, KeyboardArrowDown } from "@mui/icons-material";
import d from "final-form-arrays";
import Flx from "../../../../components/layout/Flx";
import CellRendererAccordianLabel from "../../../../components/agGrid/cellRenderer/CellRendererAccordianLabel";

ModuleRegistry.registerModules([
  SideBarModule,
  RowGroupingPanelModule,
  MultiFilterModule,
  RowGroupingModule,
  PivotModule,
  TreeDataModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  SetFilterModule,
]);

const LoanPropertiesTable = forwardRef(
  (
    {
      rowData,
      onRowClicked,
      showAllColumns,
      selectedId,
      quickFilterText,
      height,
      sx,
    },
    ref
  ) => {
    const columnDefs = useMemo(
      () => [
        {
          field: "address",
          headerName: "Address",
          flex: 1,
          cellStyle: {
            paddingTop: "8px",
            paddingBottom: "8px",
            paddingRight: "24px",
          },
          autoHeight: true,
          cellClass: null,
          // resizeable: false,
          // minWidth: 440,
          pinned: "left",
          valueFormatter: (params) => {
            return params?.value?.fullAddress || "";
          },
          cellRenderer: (params) => (
            <CellRendererAccordianLabel
              params={params}
              // value={params.data?.address?.fullAddress}
              active={params?.data?._id === selectedId}
              endContent={
                <PropertyAssociatedCommentsBadge
                  propertyId={params.data?._id}
                />
              }
            />
          ),
          // cellRenderer: (params) => {
          //   return (
          //     <Flx ac gap={1}>
          //       {params?.data?._id === selectedId ? (
          //         <KeyboardArrowDown className="thin" />
          //       ) : (
          //         <ChevronRightOutlined className="thin" />
          //       )}
          //       <span>{params.data?.address?.fullAddress}</span>
          //       <PropertyAssociatedCommentsBadge
          //         propertyId={params.data?._id}
          //       />
          //     </Flx>
          //   );
          // },
        },

        {
          field: "propertyType",
          headerName: "Property Type",
        },
        {
          field: "purchasePrice",
          headerName: "Purchase Price",
          type: "dollar",
        },
        {
          field: "purchaseDate",
          headerName: "Purchase Date",
          type: "date",
        },
        {
          field: "proposedPropertyType",
          headerName: "Proposed Property Type",
        },
        {
          field: "asIsValue",
          headerName: "As-Is Value",
          type: "dollar",
        },
        {
          field: "arv",
          headerName: "ARV",
          type: "dollar",
        },
        {
          field: "secondaryArv",
          headerName: "Secondary ARV",
          type: "dollar",
        },
        {
          field: "appraisalDate",
          headerName: "Appraisal Date",
          type: "date",
        },
        {
          field: "secondaryAppraisalDate",
          headerName: "Secondary Appraisal Date",
          type: "date",
        },

        {
          field: "subjectPropertyNotes",
          headerName: "Subject Property Notes",
        },
        {
          field: "propertyRuralIndicator",
          headerName: "Rural Indicator",
          type: "boolean",
        },
        {
          field: "unitNotes",
          headerName: "Unit Notes",
        },

        {
          field: "additionalBasis",
          headerName: "Additional Basis",
          type: "dollar",
        },
        {
          field: "assignmentFees",
          headerName: "Assignment Fees",
          type: "dollar",
        },
        {
          field: "sellersConcession",
          headerName: "Sellers Concession",
          type: "dollar",
        },

        {
          field: "propertyListedForSale",
          headerName: "Listed For Sale",
          type: "boolean",
        },
        {
          field: "lastListedForSaleDate",
          headerName: "Last Listed Date",
          type: "date",
        },
        {
          field: "lastListedForSalePrice",
          headerName: "Last Listed Price",
          type: "dollar",
        },
        {
          field: "changeOfUseFlag",
          headerName: "Change of Use",
          type: "boolean",
        },
        {
          field: "condoConversion",
          headerName: "Condo Conversion",
          type: "boolean",
        },
        {
          field: "floodZone",
          headerName: "Flood Zone",
          type: "boolean",
        },
        {
          field: "floodCertDate",
          headerName: "Flood Cert Date",
          type: "date",
        },
        {
          field: "residentialSqFootage",
          headerName: "Residential Sq Ft",
          type: "number",
        },
        {
          field: "commercialSqFootage",
          headerName: "Commercial Sq Ft",
          type: "number",
        },
        {
          field: "afterRenoSqFootage",
          headerName: "After Reno Sq Ft",
          type: "number",
        },
        {
          field: "annualTaxes",
          headerName: "Annual Taxes",
          type: "dollar",
        },
        {
          field: "annualInsurance",
          headerName: "Annual Insurance",
          type: "dollar",
        },
        {
          field: "annualFloodInsurance",
          headerName: "Annual Flood Insurance",
          type: "dollar",
        },
        {
          field: "annualHoaFees",
          headerName: "Annual HOA Fees",
          type: "dollar",
        },
        {
          field: "annualPropertyMgmtFees",
          headerName: "Annual Property Mgmt Fees",
          type: "dollar",
        },
        {
          field: "annualOtherExpense",
          headerName: "Annual Other Expense",
          type: "dollar",
        },
        {
          field: "propertyConditionAtOrigination",
          headerName: "Condition at Origination",
        },
        {
          field: "propertyStatusAtOrigination",
          headerName: "Status at Origination",
        },
        {
          field: "constructionBudget",
          headerName: "Construction Budget",
          type: "dollar",
        },
        {
          field: "financedBudgetPercent",
          headerName: "Financed Budget %",
          type: "number",
        },
        {
          field: "budgetIsFinal",
          headerName: "Budget Is Final",
          type: "boolean",
        },
        {
          field: "budgetMatchesAppraisal",
          headerName: "Budget Matches Appraisal",
          type: "boolean",
        },
        {
          field: "finalBudgetUploaded",
          headerName: "Final Budget Uploaded",
          type: "boolean",
        },
        {
          field: "finalBudgetFeasible",
          headerName: "Final Budget Feasible",
          type: "boolean",
        },

        {
          field: "costBasis",
          type: "dollar",
        },
        {
          field: "secondaryAsIsVariance",
          type: "dollar",
        },
        {
          field: "secondaryArvVariance",
          type: "dollar",
        },
        {
          field: "unitCount",
          type: "number",
        },
        {
          field: "vacantUnitCount",
          type: "number",
        },
        {
          field: "residentialUnitCount",
          type: "number",
        },
        {
          field: "commercialUnitCount",
          type: "number",
        },
        {
          field: "financedBudget",
          type: "dollar",
        },
        {
          field: "monthlyTaxes",
          type: "dollar",
        },
        {
          field: "monthlyInsurance",
          type: "dollar",
        },
        {
          field: "monthlyFloodInsurance",
          type: "dollar",
        },
        {
          field: "monthlyHoaFees",
          type: "dollar",
        },
        {
          field: "monthlyPropertyMgmtFees",
          type: "dollar",
        },
        {
          field: "monthlyOtherExpense",
          type: "dollar",
        },
        {
          field: "squareFootageChange",
          type: "percent",
        },
        {
          field: "feasibility_required",
          type: "boolean",
        },
        {
          field: "salesforceId",
          headerName: "Salesforce ID",
          hide: true,
        },

        {
          field: "last_integration_sync",
          headerName: "Last Integration Sync",
          type: "date",
          hide: true,
        },
      ],
      [selectedId]
    );

    const dynamicColumnDefs = useDynamicColumnDefs(rowData, columnDefs);

    return (
      <AgGridClickableTable
        ref={ref}
        sx={sx}
        height={height}
        rowData={rowData}
        columnDefs={dynamicColumnDefs}
        selectedId={selectedId}
        autoSize
        // autoSizeToFit
        path="properties"
        quickFilterText={quickFilterText}
        onRowClicked={onRowClicked}
      />
    );
  }
);

export default LoanPropertiesTable;
